import type { TranslationTable } from "@repo/shared/types";

const EN_GB = {
  title: "Notifications",
  bellLabel: (n: number) => (n === 0 ? "Notifications" : `Notifications, ${n} unread`),
  markAllRead: "Mark all as read",
  empty: "No notifications yet",
  loadFailed: "Could not load notifications",
  settings: "Notification settings",
};

// All 17 locales, like every other string in the top bar's bell.
export const T: TranslationTable<typeof EN_GB> = {
  en_GB: EN_GB,
  fr_FR: {
    title: "Notifications",
    bellLabel: (n) => (n === 0 ? "Notifications" : `Notifications, ${n} non lues`),
    markAllRead: "Tout marquer comme lu",
    empty: "Aucune notification pour l'instant",
    loadFailed: "Impossible de charger les notifications",
    settings: "Paramètres des notifications",
  },
  es_ES: {
    title: "Notificaciones",
    bellLabel: (n) => (n === 0 ? "Notificaciones" : `Notificaciones, ${n} sin leer`),
    markAllRead: "Marcar todo como leído",
    empty: "Aún no hay notificaciones",
    loadFailed: "No se pudieron cargar las notificaciones",
    settings: "Ajustes de notificaciones",
  },
  pt_BR: {
    title: "Notificações",
    bellLabel: (n) => (n === 0 ? "Notificações" : `Notificações, ${n} não lidas`),
    markAllRead: "Marcar todas como lidas",
    empty: "Nenhuma notificação ainda",
    loadFailed: "Não foi possível carregar as notificações",
    settings: "Configurações de notificações",
  },
  de_DE: {
    title: "Benachrichtigungen",
    bellLabel: (n) => (n === 0 ? "Benachrichtigungen" : `Benachrichtigungen, ${n} ungelesen`),
    markAllRead: "Alle als gelesen markieren",
    empty: "Noch keine Benachrichtigungen",
    loadFailed: "Benachrichtigungen konnten nicht geladen werden",
    settings: "Benachrichtigungseinstellungen",
  },
  it_IT: {
    title: "Notifiche",
    bellLabel: (n) => (n === 0 ? "Notifiche" : `Notifiche, ${n} non lette`),
    markAllRead: "Segna tutte come lette",
    empty: "Ancora nessuna notifica",
    loadFailed: "Impossibile caricare le notifiche",
    settings: "Impostazioni notifiche",
  },
  ar: {
    title: "الإشعارات",
    bellLabel: (n) => (n === 0 ? "الإشعارات" : `الإشعارات، ${n} غير مقروءة`),
    markAllRead: "تعليم الكل كمقروء",
    empty: "لا توجد إشعارات بعد",
    loadFailed: "تعذّر تحميل الإشعارات",
    settings: "إعدادات الإشعارات",
  },
  ja_JP: {
    title: "通知",
    bellLabel: (n) => (n === 0 ? "通知" : `通知、未読 ${n} 件`),
    markAllRead: "すべて既読にする",
    empty: "通知はまだありません",
    loadFailed: "通知を読み込めませんでした",
    settings: "通知設定",
  },
  hi_IN: {
    title: "सूचनाएँ",
    bellLabel: (n) => (n === 0 ? "सूचनाएँ" : `सूचनाएँ, ${n} अपठित`),
    markAllRead: "सभी को पढ़ा हुआ चिह्नित करें",
    empty: "अभी तक कोई सूचना नहीं",
    loadFailed: "सूचनाएँ लोड नहीं हो सकीं",
    settings: "सूचना सेटिंग्स",
  },
  zh_CN: {
    title: "通知",
    bellLabel: (n) => (n === 0 ? "通知" : `通知，${n} 条未读`),
    markAllRead: "全部标为已读",
    empty: "暂无通知",
    loadFailed: "无法加载通知",
    settings: "通知设置",
  },
  zh_TW: {
    title: "通知",
    bellLabel: (n) => (n === 0 ? "通知" : `通知，${n} 則未讀`),
    markAllRead: "全部標為已讀",
    empty: "尚無通知",
    loadFailed: "無法載入通知",
    settings: "通知設定",
  },
  nl_NL: {
    title: "Meldingen",
    bellLabel: (n) => (n === 0 ? "Meldingen" : `Meldingen, ${n} ongelezen`),
    markAllRead: "Alles als gelezen markeren",
    empty: "Nog geen meldingen",
    loadFailed: "Meldingen konden niet worden geladen",
    settings: "Meldingsinstellingen",
  },
  nb_NO: {
    title: "Varsler",
    bellLabel: (n) => (n === 0 ? "Varsler" : `Varsler, ${n} uleste`),
    markAllRead: "Merk alle som lest",
    empty: "Ingen varsler ennå",
    loadFailed: "Kunne ikke laste varsler",
    settings: "Varslingsinnstillinger",
  },
  sv_SE: {
    title: "Aviseringar",
    bellLabel: (n) => (n === 0 ? "Aviseringar" : `Aviseringar, ${n} olästa`),
    markAllRead: "Markera alla som lästa",
    empty: "Inga aviseringar ännu",
    loadFailed: "Det gick inte att läsa in aviseringar",
    settings: "Aviseringsinställningar",
  },
  fi_FI: {
    title: "Ilmoitukset",
    bellLabel: (n) => (n === 0 ? "Ilmoitukset" : `Ilmoitukset, ${n} lukematonta`),
    markAllRead: "Merkitse kaikki luetuiksi",
    empty: "Ei vielä ilmoituksia",
    loadFailed: "Ilmoituksia ei voitu ladata",
    settings: "Ilmoitusasetukset",
  },
  da_DK: {
    title: "Notifikationer",
    bellLabel: (n) => (n === 0 ? "Notifikationer" : `Notifikationer, ${n} ulæste`),
    markAllRead: "Markér alle som læst",
    empty: "Ingen notifikationer endnu",
    loadFailed: "Notifikationerne kunne ikke indlæses",
    settings: "Indstillinger for notifikationer",
  },
  is_IS: {
    title: "Tilkynningar",
    bellLabel: (n) => (n === 0 ? "Tilkynningar" : `Tilkynningar, ${n} ólesnar`),
    markAllRead: "Merkja allt sem lesið",
    empty: "Engar tilkynningar enn",
    loadFailed: "Ekki tókst að hlaða tilkynningum",
    settings: "Stillingar tilkynninga",
  },
};
